import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import { BuscaPostoDialogComponent } from '../busca-posto-dialog/busca-posto-dialog.component';
import { ColarDialogComponent } from '../colar-dialog/colar-dialog.component';
import { PostoItem, PostoResponse } from '../rede-especial.interface';
import { RedeEspecialService } from '../rede-especial.service';

/**
 * Interface que define a estrutura de um posto
 */
export interface Posto {
  codPosto: number; // Código identificador do posto
  nomePosto?: string; // Nome reduzido do posto
  nomeReduzido?: string;
  descontoAcrescimo?: number;
  precoUnitario?: number;
  precoFinal?: number;
}

/**
 * Interface para erros de importação de postos
 */
export interface PostoErro {
  linha: number; // Número da linha onde ocorreu o erro
  mensagem: string; // Mensagem descritiva do erro
  codPosto?: string; // Código do posto com erro (opcional)
  tipoErro?: string; // Tipo de erro (duplicidade, formato inválido, etc.)
}

/**
 * Componente para seleção de postos
 */
@Component({
  selector: 'app-seletor-postos',
  templateUrl: './seletor-postos.component.html',
  styleUrls: ['./seletor-postos.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class SeletorPostosComponent implements OnInit {
  @Input() titulo: string = 'Postos';
  @Input() required: boolean = false;
  @Input() readonly: boolean = false;
  @Input() tamanhoPagina: number = 10;
  @Input() alturaTabela: string = '170px';
  @Input() mostrarErros: boolean = true;

  @Output() postosAlterados = new EventEmitter<Posto[]>();

  // Form control para o código do posto
  postoControl = new FormControl({ value: '', disabled: false });

  /** Lista de postos selecionados */
  _postosSelecionados: Posto[] = [];

  @Input() set postosSelecionados(value: Posto[]) {
    this._postosSelecionados = value || [];
    this.atualizarExibicao();
  }

  get postosSelecionados(): Posto[] {
    return this._postosSelecionados;
  }

  /** Lista de postos exibidos na tabela com paginação */
  postosExibidos: Posto[] = [];

  /** Número atual da página de postos */
  paginaAtual: number = 0;

  /** Indica se está carregando mais postos */
  carregandoMaisPostos: boolean = false;

  /** Lista de erros na importação de postos */
  postosSelecionadosErros: PostoErro[] = [];

  /** Nome reduzido do posto para exibição no input */
  postoNomeReduzido: string = '';

  /** Colunas da tabela de postos */
  displayedColumns: string[] = ['codPosto'];

  /** Elemento de referência para o container de scroll */
  @ViewChild('postosScrollContainer') postosScrollContainer!: ElementRef;

  mostrarPostos = false;

  private postoService = inject(RedeEspecialService);

  constructor(
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    if (this.readonly) {
      this.postoControl.disable();
    }

    if (this._postosSelecionados.length > 0) {
      this.atualizarExibicao();
    }
  }

  /**
   * Filtra o valor do input e abre o diálogo de busca de posto
   */
  filtrarEAbrirDialogPosto(): void {
    if (this.readonly) return;

    // Obtém o valor atual do input
    const valorInput = this.postoControl.value
      ? this.postoControl.value.toString().trim()
      : '';

    // Configurações padrão para o diálogo
    let paramType = 'razaoSocial';
    let paramValue = valorInput;

    // Verifica se o input contém apenas números
    if (valorInput) {
      // Extrai os números do input (pode conter formato "123 - Nome do Posto")
      const numerosPossiveis = valorInput.match(/\d+/g);

      if (numerosPossiveis && numerosPossiveis.length > 0) {
        // Se encontrou números, assume que é um código de posto
        paramType = 'codPosto';
        paramValue = numerosPossiveis[0];
      }
    }

    this.dialog.open(BuscaPostoDialogComponent, {
      id: 'lupa',
      width: '90vw',
      maxWidth: '900px',
      height: '80vh',
      maxHeight: '600px',
      data: {
        mult: true,
        callback: (postos: any[]) => {
          let countAdicionados = 0;
          let countExistentes = 0;

          postos.forEach((p) => {
            const posto = {
              codPosto: Number(p.codPosto),
              nomeReduzido: p.nomePosto,
            };

            const postoExistente = this._postosSelecionados.find(
              (ps) => ps.codPosto === posto.codPosto,
            );

            if (!postoExistente) {
              this._postosSelecionados.push(posto);
              countAdicionados++;

              this.atualizarExibicao();
              this.postoNomeReduzido = `${p.codPosto} - ${p.nomePosto}`;
              this.postosAlterados.emit(this._postosSelecionados);
            } else {
              countExistentes++;
            }
          });

          // Fecha o dialog UMA VEZ
          this.dialog.getDialogById('lupa')?.close();

          // Mensagem de adicionados
          if (countAdicionados > 0) {
            const texto =
              countAdicionados === 1
                ? '1 Posto adicionado com sucesso'
                : `${countAdicionados} Postos adicionados com sucesso`;

            this.snackBar.open(texto, 'Fechar', {
              duration: 5000,
              panelClass: ['success-snackbar'],
            });
          }

          // Mensagem de já existentes
          if (countExistentes > 0) {
            const texto =
              countExistentes === 1
                ? '1 Posto já estava na lista'
                : `${countExistentes} Postos já estavam na lista`;

            this.snackBar.open(texto, 'Fechar', {
              duration: 5000,
              panelClass: ['warning-snackbar'],
            });
          }
        },
      },
    });

    // Acessa a instância do componente do diálogo para definir valores iniciais
    // Isso será feito em um setTimeout para garantir que o componente foi instanciado
    setTimeout(() => {
      const dialogComponent = this.dialog.openDialogs[0]
        ?.componentInstance as any;
      if (dialogComponent && dialogComponent.formSearch) {
        // Define o tipo de parâmetro (código ou texto)
        dialogComponent.formSearch.get('paramType')?.setValue(paramType);

        // Define o valor do parâmetro se tiver algum
        if (paramValue) {
          dialogComponent.formSearch.get('paramValue')?.setValue(paramValue);

          // Se tiver um valor válido, realiza a busca automaticamente
          if (paramValue.length > 0) {
            dialogComponent.search();
          }
        }
      }
    }, 300);
  }

  /**
   * Abre o diálogo para busca e seleção de posto
   */
  openDialogPosto(): void {
    if (this.readonly) return;

    this.dialog.open(BuscaPostoDialogComponent, {
      width: '90vw',
      maxWidth: '900px',
      height: '80vh',
      maxHeight: '600px',
      data: (codPosto: number, nomePosto: string) => {
        const posto = {
          codPosto: Number(codPosto),
          nomeReduzido: nomePosto || '',
        };

        // Verifica se o posto já existe na lista
        const postoExistente = this._postosSelecionados.find(
          (p) => p.codPosto === posto.codPosto,
        );

        if (!postoExistente) {
          // Adiciona o posto à lista se não existir
          this._postosSelecionados.push(posto);

          // Atualiza a exibição
          this.atualizarExibicao();

          // Atualiza o nome reduzido para exibição no campo de input
          this.postoNomeReduzido = `${codPosto} - ${nomePosto}`;

          this.snackBar.open(
            `Posto ${codPosto} - ${nomePosto} adicionado com sucesso`,
            'Fechar',
            {
              duration: 5000,
              panelClass: ['success-snackbar'],
            },
          );

          // Emite evento com a lista atualizada
          this.postosAlterados.emit(this._postosSelecionados);
        } else {
          this.snackBar.open(`Posto ${codPosto} já está na lista`, 'Fechar', {
            duration: 5000,
            panelClass: ['warning-snackbar'],
          });
        }

        //this.dialog.closeAll();
      },
    });
  }

  /**
   * Abre o diálogo para colar múltiplos postos
   */
  openDialogMultiplosPostos(): void {
    if (this.readonly) return;

    const dialogRef = this.dialog.open(ColarDialogComponent, {
      width: '90vw',
      maxWidth: '500px',
      height: '80vh',
      maxHeight: '600px',
      disableClose: false,
      data: {
        titulo: 'Importar Múltiplos Postos',
        descricao: 'Cole aqui a lista de códigos de postos, um por linha.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.trim()) {
        this.processarMultiplosPostos(result);
      }
    });
  }

  /**
   * Processa o texto colado com múltiplos postos
   * @param textoCodigos Texto contendo códigos de postos separados por linhas
   */
  processarMultiplosPostos(textoCodigos: string): void {
    // Limpa apenas os erros, não a lista de postos
    this.postosSelecionadosErros = [];

    const linhas = textoCodigos
      .split(/\r?\n/)
      .filter((linha) => linha.trim() !== '');
    const erros: PostoErro[] = [];
    const postosParaBuscar: number[] = [];
    const postosProcessados = new Set<number>();

    // Primeiro verifica erros de formato e duplicidade
    linhas.forEach((linha, index) => {
      const numeroLinha = index + 1;
      const codPosto = Number(linha.trim());

      if (isNaN(codPosto) || codPosto <= 0) {
        erros.push({
          linha: numeroLinha,
          mensagem: 'Código de posto inválido',
          codPosto: linha.trim(),
          tipoErro: 'FORMATO_INVALIDO',
        });
      } else {
        // Verifica se o posto já existe na lista
        const postoExistente = this._postosSelecionados.find(
          (p) => p.codPosto === codPosto,
        );

        // Verifica se o posto já foi processado (duplicado no texto colado)
        if (postosProcessados.has(codPosto)) {
          erros.push({
            linha: numeroLinha,
            mensagem: 'Posto já está na lista processada',
            codPosto: codPosto.toString(),
            tipoErro: 'DUPLICIDADE',
          });
        } else if (postoExistente) {
          erros.push({
            linha: numeroLinha,
            mensagem: 'Posto já está na lista',
            codPosto: codPosto.toString(),
            tipoErro: 'DUPLICIDADE',
          });
        } else {
          // Adiciona o posto para buscar detalhes na API
          postosParaBuscar.push(codPosto);
          postosProcessados.add(codPosto);
        }
      }
    });

    this.postosSelecionadosErros = erros;

    if (postosParaBuscar.length > 0) {
      // Busca detalhes dos postos na API
      this.postoService.buscarMultiplosPosto(postosParaBuscar).subscribe({
        next: (response: PostoResponse) => {
          let postosAdicionados = 0;
          let postosNaoEncontrados = 0;
          console.log('TESTE', response);
          console.log('postosParaBuscar', postosParaBuscar);
          console.log('response.retorno', response.retorno);
          if (postosParaBuscar.length > response.retorno.length) {
            console.log('postosParaBuscar', postosParaBuscar);
            console.log('response.retorno', response.retorno);
            this.mostrarPostos = false;
            let val = postosParaBuscar.filter(
              (p) => !response.retorno.some((r) => r.codPosto == p),
            );
            val.forEach((p) => {
              erros.push({
                linha:
                  erros.length + postosNaoEncontrados + postosAdicionados + 1,
                mensagem: 'Posto não encontrado',
                codPosto: p.toString(),
                tipoErro: 'NAO_ENCONTRADO',
              });

              postosNaoEncontrados++;
            });
          }
          // Processa cada resposta da API
          response.retorno.forEach((posto: PostoItem, index: number) => {
            const codPosto = postosParaBuscar[index];
            if (posto) {
              // Adiciona o posto com todos os detalhes
              this._postosSelecionados.push({
                codPosto: posto.codPosto,
                nomePosto: posto.nomePosto || '',
              });

              postosAdicionados++;
            } else {
              // Se a API não retornou dados, registra erro e NÃO adiciona à lista
              erros.push({
                linha:
                  erros.length + postosNaoEncontrados + postosAdicionados + 1,
                mensagem: 'Posto não encontrado',
                codPosto: codPosto.toString(),
                tipoErro: 'NAO_ENCONTRADO',
              });

              postosNaoEncontrados++;
            }
          });

          // Atualiza os erros
          this.postosSelecionadosErros = erros;

          // Atualiza a exibição da lista
          if (postosAdicionados > 0) {
            // Atualiza a exibição
            this.atualizarExibicao();

            // Limpa o nome reduzido do posto individual
            this.postoNomeReduzido = '';

            this.snackBar.open(
              `${postosAdicionados} postos adicionados à lista. ${postosNaoEncontrados} postos não encontrados.`,
              'OK',
              {
                duration: 3000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['snackbar-success'],
              },
            );

            // Emite evento com a lista atualizada
            this.postosAlterados.emit(this._postosSelecionados);
          } else if (postosNaoEncontrados > 0) {
            this.snackBar.open(
              `Nenhum posto adicionado. ${postosNaoEncontrados} postos não foram encontrados na base de dados.`,
              'OK',
              {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                panelClass: ['snackbar-warning'],
              },
            );
          }
        },
        error: (error: any) => {
          console.error('Erro ao buscar detalhes dos postos:', error);

          // Em caso de erro na requisição, registra erro para todos os códigos e NÃO adiciona à lista
          postosParaBuscar.forEach((codPosto, index) => {
            erros.push({
              linha: erros.length + index + 1,
              mensagem: 'Erro ao buscar dados do posto',
              codPosto: codPosto.toString(),
              tipoErro: 'ERRO_API',
            });
          });

          // Atualiza os erros
          this.postosSelecionadosErros = erros;

          this.snackBar.open(
            `Erro ao buscar detalhes dos postos. Nenhum posto foi adicionado.`,
            'OK',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'bottom',
              panelClass: ['snackbar-error'],
            },
          );
        },
      });
    }
  }

  /**
   * Manipula o evento de scroll na lista de postos
   * @param event Evento de scroll
   */
  onPostosScroll(event: any): void {
    const element = event.target;
    const chegouAoFinal =
      element.scrollHeight - element.scrollTop - element.clientHeight < 20;

    if (chegouAoFinal && !this.carregandoMaisPostos && this.temMaisPostos()) {
      this.carregarMaisPostos();
    }
  }

  /**
   * Verifica se existem mais postos para carregar
   * @returns Verdadeiro se ainda houver postos não exibidos
   */
  temMaisPostos(): boolean {
    return this.postosExibidos.length < this._postosSelecionados.length;
  }

  /**
   * Carrega mais postos para exibição na tabela
   */
  carregarMaisPostos(): void {
    this.carregandoMaisPostos = true;

    setTimeout(() => {
      const inicio = this.postosExibidos.length;
      const fim = inicio + this.tamanhoPagina;

      if (inicio < this._postosSelecionados.length) {
        const novosPostos = this._postosSelecionados.slice(inicio, fim);
        this.postosExibidos = [...this.postosExibidos, ...novosPostos];
        this.postosExibidos.sort((a, b) => a.codPosto - b.codPosto);
        this.paginaAtual++;
      }

      this.carregandoMaisPostos = false;
    }, 300);
  }

  /**
   * Atualiza a exibição da lista de postos
   */
  atualizarExibicao(): void {
    this.postosExibidos = [];
    this.paginaAtual = 0;
    this.carregarMaisPostos();
  }

  /**
   * Limpa a lista de postos
   */
  limparPostos(): void {
    if (this.readonly) return;

    this._postosSelecionados = [];
    this.postosExibidos = [];
    this.postosSelecionadosErros = [];
    this.paginaAtual = 0;
    this.carregandoMaisPostos = false;
    this.postoNomeReduzido = '';

    // Notifica o usuário
    this.snackBar.open('Todos os postos foram removidos', 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['snackbar-success'],
    });

    // Emite evento com a lista vazia
    this.postosAlterados.emit(this._postosSelecionados);
  }

  /**
   * Limpa os erros da importação de postos
   */
  limparErrosImportacao(): void {
    this.postosSelecionadosErros = [];

    // Notifica o usuário
    this.snackBar.open('Erros da importação de postos foram limpos', 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['snackbar-success'],
    });
  }

  alternarVisibilidade() {
    this.mostrarPostos = !this.mostrarPostos;
  }

  removerPosto(posto: any): void {
    // remove da lista principal
    this.postosSelecionados = this.postosSelecionados.filter(
      (p) => p.codPosto !== posto.codPosto,
    );

    // remove da lista exibida
    this.postosExibidos = this.postosExibidos.filter(
      (p) => p.codPosto !== posto.codPosto,
    );

    this.postosAlterados.emit(this._postosSelecionados);
  }
}
